import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./ApplicationsPage.css";
import fetchBase from "../utils/fetchBase";
import LoadingOverlay from "../components/LoadingOverlay";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;


const PdfPage = ({ page, pageIndex, selectedTool, fields, onFieldPlace, onFieldMove, onFieldRemove }) => {
    const canvasRef = useRef(null);
    const overlayRef = useRef(null);
    const viewport = page.getViewport({ scale: 1.2 });




    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const renderTask = page.render({ canvasContext: ctx, viewport });
        return () => {
            renderTask.cancel();
        };
    }, [page, viewport.width, viewport.height]);

    return (
        <div style={{ position: "relative", width: viewport.width, margin: "0 auto" }}>
            <div style={{
                textAlign: "center",
                fontSize: "11px",
                color: "#6e6e73",
                padding: "4px 0",
                background: "#f5f5f7",
                borderBottom: "1px solid #e0e0e0"
            }}>
                Page {pageIndex + 1}
            </div>
            <canvas ref={canvasRef} />
            <div
                ref={overlayRef}
                onClick={(e) => {
                    if (!selectedTool) return;
                    if (e.target !== e.currentTarget) return;
                    const rect = overlayRef.current.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const y = e.clientY - rect.top;
                    onFieldPlace({ fieldType: selectedTool, x, y, page: pageIndex });
                }}
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    cursor: selectedTool ? "crosshair" : "default",
                    pointerEvents: selectedTool ? "auto" : "none",
                }}
            >
                {fields.filter(f => f.page === pageIndex).map((f) => (
                    <div
                        key={f.id}
                        draggable
                        onDragStart={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            e.dataTransfer.setData("text/offsetX", String(e.clientX - rect.left));
                            e.dataTransfer.setData("text/offsetY", String(e.clientY - rect.top));
                        }}
                        onDragEnd={(e) => {
                            const rect = overlayRef.current.getBoundingClientRect();
                            const labelWidth = e.currentTarget.offsetWidth;
                            const labelHeight = e.currentTarget.offsetHeight;
                            const x = e.clientX - rect.left - labelWidth / 2;
                            const y = e.clientY - rect.top - labelHeight / 2;
                            onFieldMove({ id: f.id, x, y });
                        }}
                        style={{
                            position: "absolute",
                            top: f.y,
                            left: f.x,
                            background: "white",
                            border: "1px solid #0071e3",
                            padding: "4px 6px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            color: "#0071e3",
                            cursor: "grab",
                            pointerEvents: "auto",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px"
                        }}
                    >
                        {f.fieldType.replace("_", " ")}
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                onFieldRemove(f.id);
                            }}
                            style={{
                                marginLeft: "2px",
                                color: "#ff3b30",
                                fontWeight: "700",
                                fontSize: "10px",
                                lineHeight: "1",
                                cursor: "pointer",
                                userSelect: "none"
                            }}
                        >✕</span>
                    </div>
                ))}
            </div>
        </div>
    );
};



const ApplicationsPage = () => {
    const [activeTab, setActiveTab] = useState("current");
    const [showModal, setShowModal] = useState(false);
    const [modalStep, setModalStep] = useState("position");
    const [positions, setPositions] = useState([]);
    const [selectedPosition, setSelectedPosition] = useState("");
    const [customPosition, setCustomPosition] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const [uploadedFilename, setUploadedFilename] = useState("");
    const [uploadedApplicationId, setUploadedApplicationId] = useState(null);
    const [instructions, setInstructions] = useState("");

    const [location, setLocation] = useState("");

    const [fields, setFields] = useState([]);
    const [selectedTool, setSelectedTool] = useState(null);

    const [pdfPages, setPdfPages] = useState([]);
    const [pdfDoc, setPdfDoc] = useState(null);

    const [isEditMode, setIsEditMode] = useState(false);
    const [applications, setApplications] = useState([]);

    const [savingFields, setSavingFields] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [hoveredAppId, setHoveredAppId] = useState(null);
    const [copiedAppId, setCopiedAppId] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [formBuilderFields, setFormBuilderFields] = useState([]);
    const [formBuilderLocation, setFormBuilderLocation] = useState("");
    const [formBuilderChanged, setFormBuilderChanged] = useState(false);
    const [completedApplications, setCompletedApplications] = useState([]);

    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [showSubmissionModal, setShowSubmissionModal] = useState(false);
    const [submissionView, setSubmissionView] = useState("form"); // "form" or "resume"
    const [submissionResumePages, setSubmissionResumePages] = useState([]);
    const [submissionFormFields, setSubmissionFormFields] = useState([]);

    const savedFieldsRef = useRef(null);
    const savedInstructionsRef = useRef("");

    const navigate = useNavigate();

    useEffect(() => {
        if (!uploadedFilename) return;
        const url = `${fetchBase}/api/applications/file/${uploadedFilename}?token=${localStorage.getItem("jwt_token")}`;
        pdfjsLib.getDocument({ url }).promise.then((doc) => {
            setPdfDoc(doc);
            const pagePromises = [];
            for (let i = 1; i <= doc.numPages; i++) {
                pagePromises.push(doc.getPage(i));
            }
            Promise.all(pagePromises).then((pages) => setPdfPages(pages));
        });
    }, [uploadedFilename]);

    useEffect(() => {
            fetch(`${fetchBase}/api/applications`, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    if (Array.isArray(data)) setApplications(data);
                })
                .catch((err) => console.error("Failed to fetch applications:", err));
        }, []);

        const fetchCompletedApplications = () => {
            fetch(`${fetchBase}/api/applications/temp`, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    if (Array.isArray(data)) setCompletedApplications(data);
                })
                .catch((err) => console.error("Failed to fetch completed applications:", err));
        };

        useEffect(() => {
            fetchCompletedApplications();
            const interval = setInterval(fetchCompletedApplications, 10000);
            return () => clearInterval(interval);
        }, []);

    useEffect(() => {
        if (!uploadedApplicationId || !isEditMode) return;
        fetch(`${fetchBase}/api/applications/${uploadedApplicationId}/fields`, {
            headers: {
                Authorization: "Bearer " + localStorage.getItem("jwt_token"),
            },
        })
            .then((res) => res.json())
            .then((data) => {
                console.log("RAW fields from DB:", JSON.stringify(data));
                if (Array.isArray(data)) {
                    const mapped = data.map(f => ({
                        id: f.id,
                        fieldType: f.fieldType,
                        x: f.xCoordinate ?? f.xcoordinate,
                        y: f.yCoordinate ?? f.ycoordinate,
                        page: f.pageNumber
                    }));
                    console.log("MAPPED fields for render:", JSON.stringify(mapped));
                    setFields(mapped);
                    savedFieldsRef.current = JSON.stringify(mapped);
                }
            })
            .catch((err) => console.error("Failed to fetch field mappings:", err));
    }, [uploadedApplicationId, isEditMode]);

    useEffect(() => {
            if (showModal) {
                fetch(`${fetchBase}/api/admin/positions`, {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    },
                })
                    .then((res) => res.json())
                    .then((data) => {
                        console.log("POSITIONS RESPONSE:", data);
                        if (Array.isArray(data)) {
                            setPositions(data);
                        } else {
                            setPositions([]);
                            console.error("Unexpected positions response:", data);
                        }
                    })
                    .catch((err) => console.error("Failed to fetch positions:", err));
            }
        }, [showModal]);

    const handleContinue = async () => {
        const finalPosition = selectedPosition === "other" ? customPosition : selectedPosition;

        if (!finalPosition) {
            alert("Please select or enter a position.");
            return;
        }

        if (selectedPosition === "other" && customPosition.trim() !== "") {
            await fetch(`${fetchBase}/api/admin/positions`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                },
                body: JSON.stringify({
                    title: customPosition.trim(),
                }),
            });
        }

        setModalStep("upload");
    };

    const handleUpload = async () => {
        const finalPosition = selectedPosition === "other" ? customPosition : selectedPosition;

        if (!selectedFile) {
            alert("Please select a PDF file to upload.");
            return;
        }

        setUploading(true);

        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("position", finalPosition);
        formData.append("instructions", instructions);
        formData.append("location", location);


        try {
            const res = await fetch(`${fetchBase}/api/applications/upload`, {
                method: "POST",
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                },
                body: formData,
            });

            if (res.ok) {
                const data = await res.json();
                setUploadedFilename(data.filename);
                setUploadedApplicationId(data.id);
                setModalStep("review");
            } else {
                alert("Upload failed. Please try again.");
            }
        } catch (err) {
            console.error("Upload error:", err);
            alert("An error occurred during upload.");
        } finally {
            setUploading(false);
        }
    };

    const handleSaveFieldMappings = async () => {
        if (!uploadedApplicationId) return;

        setSavingFields(true);
        setSaveSuccess(false);

        console.log("fields before save:", JSON.stringify(fields));

        const payload = fields.map(f => ({
            applicationId: uploadedApplicationId,
            fieldType: f.fieldType,
            pageNumber: f.page,
            xCoordinate: parseFloat(f.x),
            yCoordinate: parseFloat(f.y),
        }));

        await fetch(`${fetchBase}/api/applications/${uploadedApplicationId}/fields`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
            },
            body: JSON.stringify(payload),
        });

        console.log("PATCH URL:", `${fetchBase}/api/applications/${uploadedApplicationId}/instructions`);
        await fetch(`${fetchBase}/api/applications/${uploadedApplicationId}/instructions`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
            },
            body: JSON.stringify({ instructions, location }),

        });

        setSaveSuccess(true);
        savedFieldsRef.current = JSON.stringify(fields);
        savedInstructionsRef.current = instructions;
        setHasUnsavedChanges(false);
        const appsRes = await fetch(`${fetchBase}/api/applications`, {
            headers: {
                Authorization: "Bearer " + localStorage.getItem("jwt_token"),
            },
        });
        const appsData = await appsRes.json();
        if (Array.isArray(appsData)) setApplications(appsData);

        setTimeout(() => {
            setSavingFields(false);
            setSaveSuccess(false);
        }, 1500);
    };

        const handleDuplicateApplication = async (id) => {
            try {
                const res = await fetch(`${fetchBase}/api/applications/created-form/${id}/duplicate`, {
                    method: "POST",
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    },
                });
                if (res.ok) {
                    const appsRes = await fetch(`${fetchBase}/api/applications`, {
                        headers: {
                            Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                        },
                    });
                    const appsData = await appsRes.json();
                    if (Array.isArray(appsData)) setApplications(appsData);
                } else {
                    alert("Failed to duplicate application. Please try again.");
                }
            } catch (err) {
                console.error("Duplicate error:", err);
                alert("An error occurred while duplicating.");
            }
        };

        const handleCloseModal = () => {

        setShowModal(false);
        setModalStep("position");
        setSelectedPosition("");
        setCustomPosition("");
        setSelectedFile(null);
        setUploadedFilename("");
        setUploadedApplicationId(null);
        setInstructions("");
        setFields([]);
        setSelectedTool(null);
        setSavingFields(false);
        setSaveSuccess(false);
        setIsEditMode(false);
        setHasUnsavedChanges(false);
        setFormBuilderFields([]);
        setFormBuilderLocation("");
        setFormBuilderChanged(false);
    };

    return (
        <>
            <nav className="apple-global-nav">
                <div className="left-nav">
                    <img src="/images/logoPlaceholder1.png" alt="Logo" className="apple-logo" />
                </div>
                <div className="center-nav">
                    <a href="/admin-dashboard">Dashboard</a>
                </div>
            </nav>

            <div className="applications-page">
                <div className="applications-header">
                    <h1>Applications</h1>
                    <button className="create-new-btn" onClick={() => setShowModal(true)}>
                        + Create New
                    </button>
                </div>

                <div className="applications-tabs">
                    <button
                        className={`tab-btn ${activeTab === "current" ? "active" : ""}`}
                        onClick={() => setActiveTab("current")}
                    >
                        Current Applications
                    </button>
                    <button
                        className={`tab-btn ${activeTab === "completed" ? "active" : ""}`}
                        onClick={() => setActiveTab("completed")}
                    >
                        Completed Applications
                    </button>
                </div>

                <div className="tab-content">
                    {activeTab === "current" && (
                        <div className="recent-uploads-grid">
                            {applications.length === 0 ? (
                                <p className="empty-state">No applications created yet.</p>
                            ) : (
                                applications.map((app) => (
                                    <div
                                        key={app.id}
                                        onMouseEnter={() => setHoveredAppId(app.id)}
                                        onMouseLeave={() => setHoveredAppId(null)}
                                        style={{
                                            padding: "16px",
                                            borderRadius: "10px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f5f5f7",
                                            cursor: "pointer",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "6px",
                                            position: "relative"
                                        }}
                                    >
                                        <div
                                                onClick={async () => {
                                                    if (app.active) {
                                                        if (!window.confirm("This application is currently active. Editing it will deactivate it and you will need to reactivate it manually. Do you want to continue?")) return;
                                                        fetch(`${fetchBase}/api/applications/${app.id}/active`, {
                                                            method: "PATCH",
                                                            headers: {
                                                                "Content-Type": "application/json",
                                                                Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                                                            },
                                                            body: JSON.stringify({ active: false }),
                                                        }).then(() => {
                                                            setApplications(prev =>
                                                                prev.map(a => a.id === app.id ? { ...a, active: false } : a)
                                                            );
                                                        });
                                                    }
                                                    setUploadedApplicationId(app.id);
                                                    setSelectedPosition(app.position);
                                                    setIsEditMode(true);
                                                    setShowModal(true);

                                                    if (app.type === "CREATED_APP") {
                                                        // Load existing fields into form builder
                                                        const fieldsRes = await fetch(`${fetchBase}/api/applications/created-form/${app.id}/fields`, {
                                                            headers: { Authorization: "Bearer " + localStorage.getItem("jwt_token") },
                                                        });
                                                        const fieldsData = await fieldsRes.json();
                                                        // Split defaults from custom
                                                        const custom = fieldsData.filter(f => !f.default);
                                                        setFormBuilderFields(custom.map(f => ({
                                                            fieldLabel: f.fieldLabel,
                                                            isDefault: false,
                                                            type: f.fieldType === "section" ? "section" : "field"
                                                        })));
                                                        setFormBuilderLocation(app.location || "");
                                                        setModalStep("formBuilder");
                                                    } else {
                                                        setUploadedFilename(app.filename);
                                                        setInstructions(app.instructions || "");
                                                        setLocation(app.location || "");
                                                        setModalStep("review");
                                                    }
                                                }}
                                            >
                                            <p style={{ fontWeight: "600", fontSize: "14px", margin: 0, color: "#1d1d1f" }}>
                                                {app.position}{app.location ? ` — ${app.location}` : ""}
                                            </p>

                                            <p style={{ fontSize: "12px", color: "#6e6e73", margin: 0 }}>{app.dateCreated}</p>
                                        </div>

                                            {hoveredAppId === app.id && (
                                                <div style={{
                                                    position: "absolute",
                                                    top: "10px",
                                                    left: "10px",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "4px"
                                                }}>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const link = `${window.location.origin}/apply?applicationId=${app.id}`;
                                                            navigator.clipboard.writeText(link);
                                                            setCopiedAppId(app.id);
                                                            setTimeout(() => setCopiedAppId(null), 2000);
                                                        }}
                                                        style={{
                                                            padding: "3px 8px",
                                                            fontSize: "10px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #ccc",
                                                            background: copiedAppId === app.id ? "#34c759" : "#fff",
                                                            color: copiedAppId === app.id ? "#fff" : "#1d1d1f",
                                                            cursor: "pointer",
                                                            transition: "background 0.2s, color 0.2s"
                                                        }}
                                                    >
                                                        {copiedAppId === app.id ? "Link Copied" : "Copy Link"}
                                                    </button>
                                                    {app.type === "CREATED_APP" && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDuplicateApplication(app.id);
                                                            }}
                                                            style={{
                                                                padding: "3px 8px",
                                                                fontSize: "10px",
                                                                borderRadius: "4px",
                                                                border: "1px solid #ccc",
                                                                background: "#fff",
                                                                color: "#1d1d1f",
                                                                cursor: "pointer",
                                                                transition: "background 0.2s, color 0.2s"
                                                            }}
                                                        >
                                                            Duplicate
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            {hoveredAppId === app.id && (
                                                <div style={{
                                                    position: "absolute",
                                                    top: "10px",
                                                    right: "10px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "6px"
                                                }}>
                                                    <span style={{ fontSize: "11px", color: app.active ? "#34c759" : "#6e6e73" }}>
                                                        {app.active ? "Active" : "Inactive"}
                                                    </span>
                                                    <div
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const newActive = !app.active;
                                                            fetch(`${fetchBase}/api/applications/${app.id}/active`, {
                                                                method: "PATCH",
                                                                headers: {
                                                                    "Content-Type": "application/json",
                                                                    Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                                                                },
                                                                body: JSON.stringify({ active: newActive }),
                                                            }).then(() => {
                                                                setApplications(prev =>
                                                                    prev.map(a => a.id === app.id ? { ...a, active: newActive } : a)
                                                                );
                                                            });
                                                        }}
                                                        style={{
                                                            width: "32px",
                                                            height: "18px",
                                                            borderRadius: "4px",
                                                            background: app.active ? "#34c759" : "#ccc",
                                                            cursor: "pointer",
                                                            position: "relative",
                                                            transition: "background 0.2s"
                                                        }}
                                                    >
                                                        <div style={{
                                                            position: "absolute",
                                                            top: "2px",
                                                            left: app.active ? "16px" : "2px",
                                                            width: "14px",
                                                            height: "14px",
                                                            borderRadius: "3px",
                                                            background: "white",
                                                            transition: "left 0.2s"
                                                        }} />
                                                    </div>
                                                </div>
                                            )}

                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === "completed" && (
                        <div className="recent-uploads-grid">
                            {completedApplications.length === 0 ? (
                                <p className="empty-state">No completed applications yet.</p>
                            ) : (
                                completedApplications.map((submission, idx) => (
                                    <div
                                        key={idx}
                                        onClick={async () => {
                                            setSelectedSubmission(submission);
                                            setSubmissionView("form");
                                            setSubmissionResumePages([]);

                                            // Load form field definitions
                                            const fieldsRes = await fetch(`${fetchBase}/api/applications/created-form/${submission._applicationId}/fields`, {
                                                headers: { Authorization: "Bearer " + localStorage.getItem("jwt_token") },
                                            });
                                            const fieldsData = await fieldsRes.json();
                                            setSubmissionFormFields(fieldsData);
                                            setShowSubmissionModal(true);
                                        }}
                                        style={{
                                            padding: "16px",
                                            borderRadius: "10px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f5f5f7",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "6px",
                                            position: "relative",
                                            cursor: "pointer"
                                        }}
                                    >
                                        <p style={{ fontWeight: "600", fontSize: "14px", margin: 0, color: "#1d1d1f" }}>
                                            {submission["First Name"] || submission["FIRST_NAME"] || "Unknown"} {submission["Last Name"] || submission["LAST_NAME"] || ""}
                                        </p>
                                        <p style={{ fontSize: "12px", color: "#6e6e73", margin: 0 }}>
                                            {submission._position || ""}
                                        </p>
                                        <p style={{ fontSize: "11px", color: "#aaa", margin: 0 }}>
                                            {submission._filename || ""}
                                        </p>
                                    </div>
                                ))
                            )}
                        </div>
                        )}
                    </div>
                </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className={`modal-box ${modalStep === "review" ? "review-mode" : ""} ${modalStep === "upload" ? "upload-mode" : ""}`}>
                        <button className="modal-close" onClick={() => {
                            if (modalStep === "review" && fields.length > 0 && fields.length < 4 && !saveSuccess) {
                                if (!window.confirm("You have unsaved field placements. Are you sure you want to close?")) return;
                            }
                            handleCloseModal();
                        }}>✕</button>

                        {modalStep === "position" && (
                            <>
                                <h3>Create New Application</h3>

                                <label>Select a Position</label>
                                <select
                                    value={selectedPosition}
                                    onChange={(e) => setSelectedPosition(e.target.value)}
                                    style={{
                                        width: "100%",
                                        padding: "6px",
                                        borderRadius: "6px",
                                        border: "1px solid #ccc",
                                        marginBottom: "12px"
                                    }}
                                >
                                    <option value="">-- Select --</option>
                                    {positions.map((pos, idx) => (
                                        <option key={idx} value={typeof pos === "object" ? pos.title : pos}>{typeof pos === "object" ? pos.title : pos}</option>
                                    ))}
                                    <option value="other">Add a position...</option>
                                </select>

                                {selectedPosition === "other" && (
                                    <>
                                        <label>Enter Position</label>
                                        <input
                                            type="text"
                                            value={customPosition}
                                            onChange={(e) => setCustomPosition(e.target.value)}
                                            placeholder="e.g. Physical Therapist"
                                        />
                                    </>
                                )}

                                <button
                                    onClick={handleContinue}
                                    style={{
                                        marginTop: "15px",
                                        padding: "8px 16px",
                                        backgroundColor: "#1d1d1f",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        width: "100%"
                                    }}
                                >
                                    Upload Application
                                </button>

                                <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "12px 0" }}>
                                    <div style={{ flex: 1, height: "1px", background: "#e0e0e0" }} />
                                    <span style={{ fontSize: "11px", color: "#6e6e73" }}>or</span>
                                    <div style={{ flex: 1, height: "1px", background: "#e0e0e0" }} />
                                </div>

                                <button
                                    onClick={() => {
                                        const finalPosition = selectedPosition === "other" ? customPosition : selectedPosition;
                                        if (!finalPosition) {
                                            alert("Please select or enter a position first.");
                                            return;
                                        }
                                        setModalStep("formBuilder");
                                    }}
                                    style={{
                                        padding: "8px 16px",
                                        backgroundColor: "#fff",
                                        color: "#0071e3",
                                        border: "1px solid #0071e3",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        width: "100%",
                                        fontSize: "13px",
                                        fontWeight: "500"
                                    }}
                                >
                                    Create Application Form
                                </button>
                            </>
                        )}

                        {modalStep === "formBuilder" && (
                <>
                    <h3 style={{ color: "#1d1d1f", marginBottom: "4px" }}>Create Application Form</h3>
                    <p style={{ fontSize: "13px", color: "#6e6e73", marginBottom: "12px" }}>
                        Position: <strong>{selectedPosition === "other" ? customPosition : selectedPosition}</strong>
                    </p>

                    <label style={{ fontSize: "13px", fontWeight: "500", marginBottom: "6px", display: "block" }}>
                        Work Location
                    </label>
                    <input
                    type="text"
                    value={formBuilderLocation}
                    onChange={(e) => { setFormBuilderLocation(e.target.value); setFormBuilderChanged(true); }}
                    placeholder='e.g. "Dallas, TX"'
                        style={{
                            width: "100%",
                            padding: "8px",
                            borderRadius: "6px",
                            border: "1px solid #ccc",
                            marginBottom: "16px",
                            boxSizing: "border-box"
                        }}
                    />

                    <div style={{
                        overflowY: "auto",
                        maxHeight: "320px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        marginBottom: "12px"
                    }}>
                        {/* Default locked fields */}
                            <div style={{
                                fontWeight: "700",
                                fontSize: "14px",
                                color: "#1d1d1f",
                                margin: "8px 0 0 0",
                                borderBottom: "1px solid #e0e0e0",
                                paddingBottom: "4px"
                            }}>
                                Basic Information
                            </div>
                            {["First Name", "Last Name", "Email", "Phone"].map((label) => (
                            <div key={label} style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 10px",
                                borderRadius: "6px",
                                border: "1px solid #e0e0e0",
                                background: "#f5f5f7",
                                fontSize: "13px",
                                color: "#6e6e73"
                            }}>
                                <span style={{ flex: 1 }}>{label}</span>
                                <span style={{ fontSize: "11px", color: "#aaa" }}>required</span>
                            </div>
                        ))}

                        {/* Custom added fields and sections */}
                            {formBuilderFields.map((field, idx) => (
                                <div key={idx} style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px"
                                }}>
                                    {field.type === "section" ? (
                                        <input
                                            type="text"
                                            value={field.fieldLabel}
                                            onChange={(e) => {
                                                const updated = [...formBuilderFields];
                                                updated[idx].fieldLabel = e.target.value;
                                                setFormBuilderFields(updated);
                                                setFormBuilderChanged(true);
                                            }}
                                            style={{
                                                flex: 1,
                                                padding: "8px",
                                                borderRadius: "6px",
                                                border: "1px solid #6e6e73",
                                                fontSize: "13px",
                                                fontWeight: "600",
                                                color: "#1d1d1f",
                                                background: "#f5f5f7"
                                            }}
                                        />
                                    ) : field.type === "signature" ? (
                                            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
                                                <div style={{
                                                    padding: "8px",
                                                    borderRadius: "6px",
                                                    border: "1px solid #ccc",
                                                    fontSize: "13px",
                                                    color: "#aaa",
                                                    background: "#f5f5f7"
                                                }}>
                                                    Type Full Legal Name
                                                </div>
                                                <p style={{ fontSize: "11px", color: "#8e8e93", margin: 0 }}>
                                                    Date signed will be recorded automatically upon submission.
                                                </p>
                                            </div>
                                        ) : (
                                            <input
                                                type="text"
                                                value={field.fieldLabel}
                                                onChange={(e) => {
                                                    const updated = [...formBuilderFields];
                                                    updated[idx].fieldLabel = e.target.value;
                                                    setFormBuilderFields(updated);
                                                    setFormBuilderChanged(true);
                                                }}
                                                style={{
                                                    flex: 1,
                                                    padding: "8px",
                                                    borderRadius: "6px",
                                                    border: "1px solid #0071e3",
                                                    fontSize: "13px"
                                                }}
                                            />
                                        )}
                                    <span
                                        onClick={() => { setFormBuilderFields(prev => prev.filter((_, i) => i !== idx)); setFormBuilderChanged(true); }}
                                        style={{
                                            color: "#ff3b30",
                                            fontWeight: "700",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            userSelect: "none"
                                        }}
                                    >✕</span>
                                </div>
                            ))}
                    </div>

<div
    style={{
        display: "flex",
        gap: "8px",
        marginBottom: "12px",
        flexWrap: "wrap"
    }}
>

    <button
        onClick={() => {
            setFormBuilderFields(prev => [
                ...prev,
                {
                    fieldLabel: "Field Name",
                    isDefault: false,
                    type: "field"
                }
            ]);

            setFormBuilderChanged(true);
        }}
        style={{
            padding: "8px",
            borderRadius: "6px",
            border: "1px dashed #0071e3",
            background: "#fff",
            color: "#0071e3",
            fontSize: "13px",
            cursor: "pointer",
            flex: "1 1 140px"
        }}
    >
        + Add Field
    </button>

    <button
        onClick={() => {
            setFormBuilderFields(prev => [
                ...prev,
                {
                    fieldLabel: "Section Header",
                    isDefault: false,
                    type: "section"
                }
            ]);

            setFormBuilderChanged(true);
        }}
        style={{
            padding: "8px",
            borderRadius: "6px",
            border: "1px dashed #6e6e73",
            background: "#fff",
            color: "#6e6e73",
            fontSize: "13px",
            cursor: "pointer",
            flex: "1 1 140px"
        }}
    >
        + Add Section
    </button>

    <button
        onClick={() => {
            setFormBuilderFields(prev => [
                ...prev,
                {
                    fieldLabel: "Type disclaimer note here",
                    isDefault: false,
                    type: "note"
                }
            ]);

            setFormBuilderChanged(true);
        }}
        style={{
            padding: "8px",
            borderRadius: "6px",
            border: "1px dashed #8e8e93",
            background: "#fff",
            color: "#8e8e93",
            fontSize: "13px",
            cursor: "pointer",
            flex: "1 1 140px"
        }}
    >
        + Note
    </button>

    <button
        onClick={() => {
            setFormBuilderFields(prev => [
                ...prev,
                {
                    fieldLabel: "Checkbox authorization text",
                    isDefault: false,
                    type: "checkbox"
                }
            ]);

            setFormBuilderChanged(true);
        }}
        style={{
            padding: "8px",
            borderRadius: "6px",
            border: "1px dashed #34c759",
            background: "#fff",
            color: "#34c759",
            fontSize: "13px",
            cursor: "pointer",
            flex: "1 1 140px"
        }}
    >
        + Checkbox
    </button>

    <button
        onClick={() => {
            setFormBuilderFields(prev => [
                ...prev,
                {
                    fieldLabel: "Electronic Signature",
                    isDefault: false,
                    type: "signature"
                }
            ]);

            setFormBuilderChanged(true);
        }}
        style={{
            padding: "8px",
            borderRadius: "6px",
            border: "1px dashed #ff9500",
            background: "#fff",
            color: "#ff9500",
            fontSize: "13px",
            cursor: "pointer",
            flex: "1 1 180px"
        }}
    >
        + Electronic Signature
    </button>

</div>

    {/* DONE BUTTON */}
    <button
    disabled={isEditMode && !formBuilderChanged}
    onClick={async () => {
        const finalPosition = selectedPosition === "other" ? customPosition : selectedPosition;
        const defaultFields = [
            { fieldLabel: "Basic Information", isDefault: true, type: "section" },
            { fieldLabel: "First Name", isDefault: true, type: "field" },
            { fieldLabel: "Last Name", isDefault: true, type: "field" },
            { fieldLabel: "Email", isDefault: true, type: "field" },
            { fieldLabel: "Phone", isDefault: true, type: "field" },
        ];

        const allFields = [...defaultFields, ...formBuilderFields];

        const res = await fetch(`${fetchBase}/api/applications/created-form`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + localStorage.getItem("jwt_token"),
            },
            body: JSON.stringify({
                position: finalPosition,
                location: formBuilderLocation,
                fields: allFields,
            }),
        });

        if (res.ok) {
            const data = await res.json();
            setUploadedApplicationId(data.id);
            const appsRes = await fetch(`${fetchBase}/api/applications`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("jwt_token") },
            });
            const appsData = await appsRes.json();
            if (Array.isArray(appsData)) setApplications(appsData);
            handleCloseModal();
        } else {
            alert("Failed to save form. Please try again.");
        }
    }}
    style={{
        width: "100%",
        padding: "8px 16px",
        backgroundColor: isEditMode && !formBuilderChanged ? "#ccc" : "#1d1d1f",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        cursor: isEditMode && !formBuilderChanged ? "not-allowed" : "pointer",
        fontSize: "13px",
        marginBottom: "10px"
    }}
    >
    Done
    </button>

    {/* DELETE BUTTON — ONLY IN EDIT MODE */}
    {isEditMode && (
        <button
            onClick={async () => {
                if (!window.confirm("Are you sure you want to delete this application? This cannot be undone.")) return;

                await fetch(`${fetchBase}/api/applications/created-form/${uploadedApplicationId}`, {
                    method: "DELETE",
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    },
                });

                const appsRes = await fetch(`${fetchBase}/api/applications`, {
                    headers: { Authorization: "Bearer " + localStorage.getItem("jwt_token") },
                });
                const appsData = await appsRes.json();
                if (Array.isArray(appsData)) setApplications(appsData);

                handleCloseModal();
            }}
            style={{
                width: "100%",
                padding: "8px 16px",
                backgroundColor: "#ff3b30",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px"
            }}
        >
            Delete Application
        </button>
    )}

</>
)}

{modalStep === "upload" && (
                    <>
                        <h3>Upload Application PDF</h3>
                        <p style={{ fontSize: "13px", color: "#6e6e73", marginBottom: "12px" }}>
                            Position: <strong>
                                {selectedPosition === "other" ? customPosition : selectedPosition}
                            </strong>
                        </p>

                        <label>Select PDF File</label>
                        <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setSelectedFile(e.target.files[0])}
                            style={{ width: "100%", marginBottom: "12px" }}
                        />

                        <button
                            onClick={handleUpload}
                            disabled={uploading}
                            style={{
                                marginTop: "15px",
                                padding: "8px 16px",
                                backgroundColor: "#1d1d1f",
                                color: "#fff",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                width: "100%"
                            }}
                        >
                            {uploading ? "Uploading..." : "Upload"}
                        </button>
                    </>
                )}

                {modalStep === "review" && (
                    <>
                        {savingFields && (
                            <LoadingOverlay
                                message={saveSuccess ? (isEditMode ? "Application updated successfully." : "Application created successfully.") : "Saving..."}
                                status={saveSuccess ? "success" : ""}
                            />
                        )}
                        <h3 style={{ color: isEditMode ? "#0071e3" : "#1d1d1f" }}>
                            {isEditMode ? "Edit Application" : "Review Application"}
                        </h3>

                        <p style={{ fontSize: "13px", color: "#6e6e73", marginBottom: "12px" }}>
                            Position: <strong>
                                {selectedPosition === "other" ? customPosition : selectedPosition}
                            </strong>
                        </p>

                        {!isEditMode && (
                            <div
                                style={{
                                    marginBottom: "10px",
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    background: "rgba(255,59,48,0.08)",
                                    border: "1px solid rgba(255,59,48,0.20)",
                                    color: "#ff3b30",
                                    fontSize: "12px",
                                    fontWeight: "600"
                                }}
                            >
                                Action: Place the required employee fields on the application before saving.
                            </div>
                        )}

                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }}>
                            {["FIRST_NAME", "LAST_NAME", "EMAIL", "PHONE"].map((type) => {
                                const alreadyPlaced = fields.some(f => f.fieldType === type);
                                if (alreadyPlaced) return null;
                                return (
                                    <button
                                        key={type}
                                        onClick={() => setSelectedTool(selectedTool === type ? null : type)}
                                        style={{
                                            padding: "6px 12px",
                                            borderRadius: "6px",
                                            border: "1px solid #ccc",
                                            background: selectedTool === type ? "#0071e3" : "#f5f5f7",
                                            color: selectedTool === type ? "#fff" : "#1d1d1f",
                                            cursor: "pointer",
                                            fontSize: "12px"
                                        }}
                                    >
                                        {type.replace("_", " ")}
                                    </button>
                                );
                            })}
                        </div>

                        <div
                            style={{
                                width: "100%",
                                height: "520px",
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                marginBottom: "16px",
                                overflowY: "auto",
                                position: "relative"
                            }}
                        >
                            {pdfPages.map((page, pageIndex) => (
                                <PdfPage
                                    key={pageIndex}
                                    page={page}
                                    pageIndex={pageIndex}
                                    selectedTool={selectedTool}
                                    fields={fields}
                                    onFieldPlace={(field) => {
                                        setFields((prev) => {
                                            const updated = [...prev, { id: Date.now(), ...field }];
                                            setHasUnsavedChanges(JSON.stringify(updated) !== savedFieldsRef.current);
                                            return updated;
                                        });
                                        setSelectedTool(null);
                                    }}
                                    onFieldMove={({ id, x, y }) => {
                                        setFields((prev) => {
                                            const updated = prev.map(f => f.id === id ? { ...f, x, y } : f);
                                            setHasUnsavedChanges(JSON.stringify(updated) !== savedFieldsRef.current);
                                            return updated;
                                        });
                                    }}
                                    onFieldRemove={(id) => {
                                        setFields((prev) => {
                                            const updated = prev.filter(f => f.id !== id);
                                            setHasUnsavedChanges(JSON.stringify(updated) !== savedFieldsRef.current);
                                            return updated;
                                        });
                                    }}
                                />
                            ))}
                        </div>

                        <label style={{ fontSize: "13px", fontWeight: "500", marginBottom: "6px", display: "block" }}>
                                                    Completion Instructions
                                                </label>

                                                <label style={{ fontSize: "13px", fontWeight: "500",
                        marginBottom: "6px", display: "block" }}>
                        Work Location
                        </label>
                        <input
                          type="text"
                          value={location}
                          onChange={(e) => {
                            setLocation(e.target.value);
                            setHasUnsavedChanges(true);
                          }}
                          placeholder='e.g. "Dallas, TX"'
                          style={{
                            width: "100%",
                            padding: "8px",
                            borderRadius: "6px",
                            border: "1px solid #ccc",
                            marginBottom: "16px"
                          }}
                        />


                        <button
                            onClick={handleSaveFieldMappings}
                            disabled={fields.length < 4 || (isEditMode && !hasUnsavedChanges)}
                            style={{
                                padding: "8px 16px",
                                backgroundColor: fields.length < 4 || (isEditMode && !hasUnsavedChanges) ? "#ccc" : "#1d1d1f",
                                color: "#fff",
                                border: "none",
                                borderRadius: "6px",
                                cursor: fields.length < 4 || (isEditMode && !hasUnsavedChanges) ? "not-allowed" : "pointer",
                                width: "100%"
                            }}
                        >
                            {fields.length < 4 ? `Place ${4 - fields.length} more field(s)` : "Done"}
                        </button>
                    </>
                )}
            </div>
        </div>
    )}
        {showSubmissionModal && selectedSubmission && (
        <div className="modal-overlay">
            <div className="modal-box review-mode" style={{ overflowY: "auto", maxHeight: "90vh" }}>
                <button className="modal-close" onClick={() => {
                    setShowSubmissionModal(false);
                    setSelectedSubmission(null);
                    setSubmissionView("form");
                    setSubmissionResumePages([]);
                    setSubmissionFormFields([]);
                }}>✕</button>

                <h3 style={{ color: "#1d1d1f", marginBottom: "2px" }}>
                    {selectedSubmission["First Name"] || selectedSubmission["FIRST_NAME"] || "Applicant"} {selectedSubmission["Last Name"] || selectedSubmission["LAST_NAME"] || ""}
                </h3>
                <p style={{ fontSize: "13px", color: "#6e6e73", marginBottom: "16px" }}>
                    {selectedSubmission._position || ""}
                </p>

                {/* Toggle bar */}
                <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                    <button
                        onClick={() => setSubmissionView("form")}
                        style={{
                            flex: 1,
                            padding: "8px",
                            borderRadius: "6px",
                            border: "none",
                            background: submissionView === "form" ? "#1d1d1f" : "#f5f5f7",
                            color: submissionView === "form" ? "#fff" : "#1d1d1f",
                            fontWeight: "600",
                            fontSize: "13px",
                            cursor: "pointer"
                        }}
                    >
                        Application
                    </button>
                    <button
                        onClick={async () => {
                            setSubmissionView("resume");
                            if (submissionResumePages.length === 0) {
                                // Find resume file for this submission
                                const resumeUrl = `${fetchBase}/api/applications/resume/${selectedSubmission._applicationId}?token=${localStorage.getItem("jwt_token")}`;
                                try {
                                    const doc = await pdfjsLib.getDocument({ url: resumeUrl }).promise;
                                    const pages = [];
                                    for (let i = 1; i <= doc.numPages; i++) {
                                        pages.push(await doc.getPage(i));
                                    }
                                    setSubmissionResumePages(pages);
                                } catch (e) {
                                    console.error("Failed to load resume PDF:", e);
                                }
                            }
                        }}
                        style={{
                            flex: 1,
                            padding: "8px",
                            borderRadius: "6px",
                            border: "none",
                            background: submissionView === "resume" ? "#1d1d1f" : "#f5f5f7",
                            color: submissionView === "resume" ? "#fff" : "#1d1d1f",
                            fontWeight: "600",
                            fontSize: "13px",
                            cursor: "pointer"
                        }}
                    >
                        Resume ›
                    </button>
                </div>

                {/* Form view */}
                {submissionView === "form" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "16px" }}>
                        {submissionFormFields.map((f) => (
                            f.fieldType === "section" ? (
                                <p key={f.id} style={{
                                    fontWeight: "700",
                                    fontSize: "14px",
                                    color: "#1d1d1f",
                                    margin: "8px 0 0 0",
                                    borderBottom: "1px solid #e0e0e0",
                                    paddingBottom: "4px"
                                }}>
                                    {f.fieldLabel}
                                </p>
                            ) : f.fieldType === "note" ? (
                                <p key={f.id} style={{
                                    fontSize: "11px",
                                    color: "#8e8e93",
                                    lineHeight: "1.5",
                                    marginTop: "2px",
                                    marginBottom: "4px"
                                }}>
                                    {f.fieldLabel}
                                </p>
                            ) : f.fieldType === "checkbox" ? (
                                <label key={f.id} style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "8px",
                                    fontSize: "12px",
                                    color: "#1d1d1f"
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={!!selectedSubmission[f.fieldLabel]}
                                        readOnly
                                    />
                                    <span>{f.fieldLabel}</span>
                                </label>
                            ) : f.fieldType === "signature" ? (
                                <div key={f.id} style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                    <label style={{ fontSize: "12px", color: "#6e6e73", fontWeight: "500" }}>
                                        {f.fieldLabel}
                                    </label>
                                    <div style={{
                                        padding: "8px",
                                        borderRadius: "6px",
                                        border: "1px solid #e0e0e0",
                                        background: "#f9f9f9",
                                        fontSize: "13px",
                                        color: "#1d1d1f",
                                        fontStyle: "italic"
                                    }}>
                                        {selectedSubmission[f.fieldLabel] || "—"}
                                    </div>
                                    <p style={{ fontSize: "11px", color: "#8e8e93", margin: 0 }}>
                                        Signed on: {selectedSubmission._submittedAt || "—"}
                                    </p>
                                </div>
                            ) : (
                                <div key={f.id} style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                    <label style={{ fontSize: "12px", color: "#6e6e73", fontWeight: "500" }}>
                                        {f.fieldLabel}
                                    </label>
                                    <div style={{
                                        padding: "8px",
                                        borderRadius: "6px",
                                        border: "1px solid #e0e0e0",
                                        background: "#f9f9f9",
                                        fontSize: "13px",
                                        color: "#1d1d1f"
                                    }}>
                                        {selectedSubmission[f.fieldLabel] || "—"}
                                    </div>
                                </div>
                            )
                        ))}
                    </div>
                )}

                {/* Resume view */}
                {submissionView === "resume" && (
                    <div style={{
                        width: "100%",
                        height: "520px",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        marginBottom: "16px",
                        overflowY: "auto"
                    }}>
                        {submissionResumePages.map((page, pageIndex) => (
                            <PdfPage
                                key={pageIndex}
                                page={page}
                                pageIndex={pageIndex}
                                selectedTool={null}
                                fields={[]}
                                onFieldPlace={() => {}}
                                onFieldMove={() => {}}
                                onFieldRemove={() => {}}
                            />
                        ))}
                    </div>
                )}

                {/* Accept / Reject — only on resume view */}
                {submissionView === "resume" && (
                    <div style={{ display: "flex", gap: "8px" }}>
                        <button
                            onClick={async () => {
                                // 1. Move files to final folder
                                await fetch(`${fetchBase}/api/applications/${selectedSubmission._applicationId}/accept`, {
                                    method: "POST",
                                    headers: { Authorization: "Bearer " + localStorage.getItem("jwt_token") },
                                });

                                // 2. Add candidate as employee
                                const firstName = selectedSubmission["First Name"] || selectedSubmission["FIRST_NAME"] || "";
                                const lastName = selectedSubmission["Last Name"] || selectedSubmission["LAST_NAME"] || "";
                                const email = selectedSubmission["Email"] || selectedSubmission["EMAIL"] || "";
                                const phone = selectedSubmission["Phone"] || selectedSubmission["PHONE"] || "";
                                const position = selectedSubmission._position || "";

                                const empRes = await fetch(`${fetchBase}/api/admin/add-employee`, {
                                    method: "POST",
                                    headers: {
                                        "Content-Type": "application/json",
                                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                                    },
                                    body: JSON.stringify({ firstName, lastName, email, phone, position }),
                                });

                                if (!empRes.ok) {
                                    const errText = await empRes.text();
                                    alert("Files accepted but employee creation failed: " + errText);
                                }

                                setShowSubmissionModal(false);
                                setSelectedSubmission(null);
                                setSubmissionView("form");
                                setSubmissionResumePages([]);
                                fetchCompletedApplications();
                            }}
                            style={{
                                flex: 1,
                                padding: "10px",
                                borderRadius: "6px",
                                border: "none",
                                background: "#34c759",
                                color: "#fff",
                                fontSize: "13px",
                                fontWeight: "600",
                                cursor: "pointer"
                            }}
                        >
                            Accept
                        </button>
                        <button
                            onClick={async () => {
                                await fetch(`${fetchBase}/api/applications/${selectedSubmission._applicationId}/reject`, {
                                    method: "POST",
                                    headers: { Authorization: "Bearer " + localStorage.getItem("jwt_token") },
                                });
                                setShowSubmissionModal(false);
                                setSelectedSubmission(null);
                                setSubmissionView("form");
                                setSubmissionResumePages([]);
                                fetchCompletedApplications();
                            }}
                            style={{
                                flex: 1,
                                padding: "10px",
                                borderRadius: "6px",
                                border: "none",
                                background: "#ff3b30",
                                color: "#fff",
                                fontSize: "13px",
                                fontWeight: "600",
                                cursor: "pointer"
                            }}
                        >
                            Reject
                        </button>
                    </div>
                )}
            </div>
        </div>
    )}
</>
);
};

export default ApplicationsPage;